import { ChangeDetectionStrategy, Injector, Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { combineLatest, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, skip, take, takeUntil } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import { UserInteractionOutcome } from '@nimbus-library/features/agent-assistant/models/agent-assistant.model';
import { IErrorStatus } from '@nimbus-global/features/shared/models/api/i-error-status.model';
import { ChatMediaPolicy, ChatServiceBase, ChatUrlParams } from '@nimbus-global/features/chat/models/chat-service.model';
import { ChatServiceFactory } from '@nimbus-global/features/chat/services/chat-service-factory.service';
import { ChatServiceType, IChatInfo, IChatMessage, INITIAL_MESSAGES_LIMIT_MAPPER } from '@nimbus-global/features/chat/models/chat.model';
import { buildChatApiUrl } from '@nimbus-global/features/chat/services/chat-service.utils';
import {
  ConversationMediaPolicyStatus,
  IMediaSendErrorResponse,
  LastMessageIndexRequest,
  MediaBlockReason,
  TwilioMessageRequest,
} from '@nimbus-global/features/chat/models/chat-twilio.model';
import { ChatTwilioService } from '@nimbus-global/features/chat/services/twilio/chat-twilio.service';
import { getMediaBlockReasonMessage } from '@nimbus-global/features/chat/utils/chat-media-block-reason.util';
import { removeMediaFilenameEchoes } from '@nimbus-global/features/chat/utils/chat-media-filename-echo.util';

export const ChatTitleTranslationMapper: Record<ChatServiceType, TranslationKeys> = {
  Whatsapp: TranslationKeys.ChatTitleWhatsapp,
  Sms: TranslationKeys.ChatTitleSms,
  Rcs: TranslationKeys.ChatTitleRcs,
  WebChat: TranslationKeys.ChatTitleWebChat,
};

@UntilDestroy()
@Component({
  selector: 'nimbus-chat-container',
  standalone: false,
  templateUrl: './chat-container.component.html',
  styleUrl: './chat-container.component.scss',
  providers: [ChatTwilioService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatContainerComponent implements OnInit, OnDestroy {
  @Input()
  public set chatInfo(value: IChatInfo) {
    const previousConversationId = this._chatInfo?.conversationId;
    this._chatInfo = value;

    if (previousConversationId && value?.conversationId && value?.type && previousConversationId !== value.conversationId) {
      this.onConversationChanged();
    } else if (value?.taskState === UserInteractionOutcome.Parked) {
      this.isMediaPolicyUpToDate = false;
    } else if (value?.type && !this.isMediaPolicyUpToDate && this.chatService) {
      this.isMediaPolicyUpToDate = true;
      this.loadMediaPolicy();
    }
  }

  public get chatInfo(): IChatInfo {
    return this._chatInfo;
  }

  @Input()
  public unparkDisabled: boolean = false;
  @Input()
  public unparkTooltip: TranslationKeys;
  @Output()
  public unparkRequested = new EventEmitter<void>();

  public saveDraftApiUrl: string;

  private _chatInfo: IChatInfo;
  private chatService: ChatServiceBase;
  private isMediaPolicyUpToDate = true;
  private tenantId: string = '';
  private userId: string = '';
  private readonly readMessagesDebounceMs = 500;
  private readonly mediaPolicyLimit = 100;
  private readonly voiceMessageFilenamePattern = /^voice-message_(\d+)\.ogg$/i;
  private readonly conversationChange$ = new Subject<void>();
  private readonly copyTextSubject = new ReplaySubject<string>(1);

  public copyText$ = this.copyTextSubject.asObservable();
  public loading$: Observable<boolean>;
  public messages$: Observable<IChatMessage[]>;
  public connectionState$: Observable<boolean>;
  public mediaPolicy$: Observable<ChatMediaPolicy | null>;
  public isMediaHandlingEnabled$: Observable<boolean>;

  public readonly IconWeight = IconWeight;
  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;
  public readonly ChatTitleTranslationMapper = ChatTitleTranslationMapper;

  public constructor(
    private readonly authenticationService: IAuthenticationService,
    private readonly chatServiceFactory: ChatServiceFactory,
    private readonly injector: Injector,
    private readonly translateService: TranslateService,
    private readonly toastrService: ToastrService,
  ) {}

  public ngOnInit(): void {
    const userInfo = this.authenticationService.getUserInfo();
    this.tenantId = userInfo?.tenantId ?? '';
    this.userId = userInfo?.userId ?? '';
    this.saveDraftApiUrl = buildChatApiUrl(this.chatInfo.type, 'SaveDraft', this.getUrlParams());
    this.initializeChatService();
  }

  public ngOnDestroy(): void {
    this.conversationChange$.next();
    this.conversationChange$.complete();
    this.chatService?.destroy();
    this.copyTextSubject.complete();
  }

  public onCopyTextInitialized(text: string): void {
    this.copyTextSubject.next(text);
  }

  public sendMessage(message: string): void {
    const request: TwilioMessageRequest = {
      author: this.userId,
      dateCreated: new Date().toISOString(),
      body: message,
    };
    this.chatService
      .sendMessage(buildChatApiUrl(this.chatInfo.type, 'Post', this.getUrlParams()), request)
      .pipe(untilDestroyed(this))
      .subscribe({
        error: () => {
          this.toastrService.error(this.translateService.instant(TranslationKeys.ChatWidgetMessageSendFailed));
        },
      });
  }

  public sendAttachments(files: File[]): void {
    if (!this.chatService.sendMedia) {
      return;
    }
    const apiUrl = buildChatApiUrl(this.chatInfo.type, 'Media', this.getUrlParams());
    files.forEach((file) => {
      this.chatService
        .sendMedia(apiUrl, file)
        .pipe(untilDestroyed(this))
        .subscribe({
          error: (error: IErrorStatus) => {
            this.toastrService.error(this.getAttachmentSendErrorMessage(error, file.name));
          },
        });
    });
  }

  public sendVoiceMessage(blob: Blob): void {
    if (!this.chatService.sendMedia) {
      return;
    }
    this.messages$.pipe(take(1), untilDestroyed(this)).subscribe((messages) => {
      const apiUrl = buildChatApiUrl(this.chatInfo.type, 'Media', this.getUrlParams());
      const filename = `voice-message_${this.getNextVoiceMessageIndex(messages)}.ogg`;
      // the recorder reports 'audio/ogg; codecs=opus', but media policies allow-list the bare
      // 'audio/ogg' mime type - the codecs parameter doesn't change the encoded bytes, so drop it here.
      const file = new File([blob], filename, { type: 'audio/ogg' });
      this.chatService
        .sendMedia(apiUrl, file)
        .pipe(untilDestroyed(this))
        .subscribe({
          error: (error: IErrorStatus) => {
            this.toastrService.error(this.getAttachmentSendErrorMessage(error, file.name));
          },
        });
    });
  }

  private getNextVoiceMessageIndex(messages: IChatMessage[]): number {
    const usedIndexes = messages
      .flatMap((message) => message.media ?? [])
      .map((media) => this.voiceMessageFilenamePattern.exec(media.filename ?? '')?.[1])
      .filter((match): match is string => !!match)
      .map(Number);
    return usedIndexes.length > 0 ? Math.max(...usedIndexes) + 1 : 1;
  }

  private getAttachmentSendErrorMessage(error: IErrorStatus, fileName: string): string {
    const errorBody = error?.error as unknown as IMediaSendErrorResponse | undefined;
    const blockReason = errorBody?.blockReason ?? MediaBlockReason.FileTypeNotAllowed;
    return `${fileName}: ${getMediaBlockReasonMessage(blockReason, this.translateService)}`;
  }

  private loadMediaPolicy(): void {
    if (!this.chatService.loadMediaPolicy) {
      return;
    }
    const mediaPolicyParams = this.getUrlParams();
    mediaPolicyParams.limit = this.mediaPolicyLimit;
    const mediaPolicyApiUrl = buildChatApiUrl(this.chatInfo.type, 'MediaPolicy', mediaPolicyParams);
    this.chatService.loadMediaPolicy(this.tenantId, mediaPolicyApiUrl);
  }

  private onConversationChanged(): void {
    this.conversationChange$.next();
    this.chatService?.reset();
    this.saveDraftApiUrl = buildChatApiUrl(this.chatInfo.type, 'SaveDraft', this.getUrlParams());
    this.initializeChatService();
  }

  public onLoadPrevMessagesRequested(): void {
    const apiUrl = buildChatApiUrl(this.chatInfo.type, 'Get', this.getUrlParams());
    this.chatService.receiveMessages(apiUrl, this.chatInfo.callerInfo);
  }

  private syncReadLastMessageIndex(): void {
    if (!this.chatService.markChatMessagesAsRead) {
      return;
    }
    const params = this.getUrlParams();
    const apiUrl = buildChatApiUrl(this.chatInfo.type, 'LastIndexRead', params);

    const lastMessageIndex$ = combineLatest([this.loading$, this.messages$]).pipe(
      filter(([isLoading]) => !isLoading),
      map(([, messages]) => this.getLastMessageIndex(messages)),
      filter((index): index is number => index !== null),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const firstRead$ = lastMessageIndex$.pipe(take(1));
    const subsequentReads$ = lastMessageIndex$.pipe(skip(1), debounceTime(this.readMessagesDebounceMs));

    merge(firstRead$, subsequentReads$)
      .pipe(takeUntil(this.conversationChange$), untilDestroyed(this))
      .subscribe((lastMessageIndex) => this.syncReadMessages(apiUrl, lastMessageIndex));
  }

  private getLastMessageIndex(messages: IChatMessage[]): number | null {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const lastMessageIndex = Math.max(...(messages ?? []).map((message) => message.index ?? -1));
    return lastMessageIndex >= 0 ? lastMessageIndex : null;
  }

  private syncReadMessages(apiUrl: string, lastMessageIndex: number): void {
    const request: LastMessageIndexRequest = {
      index: lastMessageIndex,
    };

    this.chatService
      .markChatMessagesAsRead(apiUrl, request)
      .pipe(untilDestroyed(this))
      .subscribe({
        error: (error) => {
          console.error(error);
        },
      });
  }

  private initializeChatService(): void {
    this.chatService = this.chatServiceFactory.createChatService(this.chatInfo.type, this.injector);
    const params = this.getUrlParams();
    const limit = INITIAL_MESSAGES_LIMIT_MAPPER[this.chatInfo.type];
    if (limit) {
      params.limit = limit;
    }
    const apiUrl = buildChatApiUrl(this.chatInfo.type, 'Get', params);
    this.chatService.initialize(apiUrl, this.chatInfo.callerInfo, this.chatInfo.conversationId, this.chatInfo.type);
    this.loadMediaPolicy();
    this.isMediaPolicyUpToDate = this.chatInfo.taskState !== UserInteractionOutcome.Parked;
    this.connectionState$ = this.chatService.connectionState$;
    this.loading$ = this.chatService.loading$;
    this.messages$ = this.chatService.messages$.pipe(map((messages) => removeMediaFilenameEchoes(messages, this.chatInfo.type)));
    this.mediaPolicy$ = this.chatService.mediaPolicy$ ?? of(null);
    this.isMediaHandlingEnabled$ = this.mediaPolicy$.pipe(
      map((mediaPolicy) => mediaPolicy?.status === ConversationMediaPolicyStatus.Available),
    );
    this.syncReadLastMessageIndex();
  }

  private getUrlParams(): ChatUrlParams {
    switch (this.chatInfo.type) {
      case ChatServiceType.Sms:
      case ChatServiceType.Rcs:
      case ChatServiceType.Whatsapp:
      case ChatServiceType.WebChat:
        return {
          tenantId: this.tenantId,
          conversationId: this.chatInfo.conversationId,
          userId: this.userId,
        };
      default:
        throw new Error(`Unsupported chat type: ${this.chatInfo.type}`);
    }
  }
}
